import React, { useState } from 'react';
import { MapPin, Phone, Clock, MessageCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const whatsappNumber = '21620505585';
  const whatsappMessage = encodeURIComponent(t('whatsapp.message'));
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simple form submission display (no backend)
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.address'),
      value: '12 rue de l\'indépendance, 2070 La Marsa, Tunisie',
      link: 'https://maps.google.com/?q=12+rue+de+l\'indépendance,+2070+La+Marsa,+Tunisie',
    },
    {
      icon: Phone,
      title: t('contact.phone'),
      value: '+216 20 505 585',
      link: 'tel:+21620505585',
    },
    {
      icon: Clock,
      title: t('contact.hours'),
      value: t('contact.hoursValue'),
      link: null,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 sm:py-20 md:py-28 bg-accent/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#00bfa5]/10 text-[#00bfa5] rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            Contact
          </span>
          <h2
            data-testid="contact-title"
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#606060] mb-3 sm:mb-4 font-['Outfit'] tracking-tight"
          >
            {t('contact.title')}
          </h2>
          <p
            data-testid="contact-subtitle"
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4"
          >
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info & Map */}
          <div className="space-y-4 sm:space-y-6">
            {/* Contact Cards */}
            <div className="grid gap-3 sm:gap-4">
              {contactInfo.map((info, index) => (
                <Card
                  key={index}
                  data-testid={`contact-info-${index}`}
                  className="bg-white border-0 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                    <div className="p-2.5 sm:p-3 rounded-xl bg-[#606060] flex-shrink-0">
                      <info.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {info.title}
                      </p>
                      {info.link ? (
                        <a
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                          className="font-medium text-[#606060] hover:text-[#00bfa5] transition-colors text-sm sm:text-base truncate block"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-medium text-[#606060] text-sm sm:text-base">{info.value}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
            >
              <Button className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-full h-12 sm:h-14 font-semibold text-base sm:text-lg flex items-center justify-center gap-2 shadow-lg">
                <MessageCircle className="w-5 h-5" />
                <span className="truncate">WhatsApp: +216 20 505 585</span>
              </Button>
            </a>

            {/* Google Map */}
            <div
              data-testid="google-map"
              className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg h-[220px] sm:h-[300px]"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3192.0!2d10.3242!3d36.8789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDUyJzQ0LjAiTiAxMMKwMTknMjcuMSJF!5e0!3m2!1sen!2stn!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="UNI CAR Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <Card
            data-testid="contact-form-card"
            className="bg-white border-0 rounded-xl sm:rounded-2xl shadow-lg"
          >
            <CardContent className="p-5 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="name" className="text-xs sm:text-sm font-medium">
                    {t('contact.form.name')}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-lg sm:rounded-xl border-border focus:border-secondary focus:ring-secondary text-base"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="email" className="text-xs sm:text-sm font-medium">
                      {t('contact.form.email')}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-form-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="rounded-lg sm:rounded-xl border-border focus:border-secondary focus:ring-secondary text-base"
                      placeholder="email@exemple.com"
                    />
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="phone" className="text-xs sm:text-sm font-medium">
                      {t('contact.form.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-form-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="rounded-lg sm:rounded-xl border-border focus:border-secondary focus:ring-secondary text-base"
                      placeholder="+216 XX XXX XXX"
                    />
                  </div>
                </div>

                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="message" className="text-xs sm:text-sm font-medium">
                    {t('contact.form.message')}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-form-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="rounded-lg sm:rounded-xl border-border focus:border-secondary focus:ring-secondary resize-none text-base"
                    placeholder={
                      language === 'fr'
                        ? 'Décrivez votre demande de réservation...'
                        : language === 'en'
                        ? 'Describe your booking request...'
                        : 'Beschreiben Sie Ihre Buchungsanfrage...'
                    }
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="w-full bg-[#606060] hover:bg-[#00bfa5] text-white rounded-full h-12 sm:h-14 font-semibold text-base sm:text-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  {t('contact.form.submit')}
                </Button>

                {submitted && (
                  <div
                    data-testid="contact-form-success"
                    className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-center font-medium text-sm sm:text-base"
                  >
                    {t('contact.form.success')}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
